import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, StyleSheet, Text, View } from 'react-native';
import * as Sentry from '@sentry/react-native';
import logger from '../../utils/logger';

// A loading screen still mounted this long after its progress animation finished
// is almost certainly stuck (its parent's navigation never fired). Real loads
// complete in seconds; even the slowest gated paths self-resolve well under a
// minute. Crossing this without unmounting is reported to Sentry as a hang.
const WATCHDOG_MS = 90000;

export default function LoadingScreen({
  title = 'Loading...',
  subtitle = 'Please wait',
  completionTitle = 'Ready!',
  completionSubtitle = 'Loading complete',
  completionMessage = 'Your content is ready to view',
  steps = [],
  mainIcon = { name: 'car', iconSet: 'MaterialCommunityIcons' },
  tip = null,
  showProgress = true,
  showTips = true,
  onComplete = null,
  useBlurBackground = false,
  blurIntensity = 15,
  // Opt-in hang detector. Pass { name, context } and if this screen is still
  // mounted after WATCHDOG_MS (i.e. the parent never navigated away), a Sentry
  // error is raised once so a silently-stuck loader becomes a visible alert.
  watchdog = null,
  blurTint = 'light',
}) {
  // Configuration validation
  let validatedTitle = title;
  let validatedSteps = steps;

  // Validate title is string
  if (typeof title !== 'string') {
    logger.warn('[LoadingScreen] Invalid config, using defaults', { title, steps });
    validatedTitle = 'Loading...';
    validatedSteps = [];
  }

  // Validate steps is array with length >= 1
  if (!Array.isArray(steps) || steps.length < 1) {
    if (typeof title === 'string') {
      // Only log if title was valid (to avoid duplicate logs)
      logger.warn('[LoadingScreen] Invalid config, using defaults', { title, steps });
    }
    validatedSteps = [];
  } else {
    // Validate each step has required fields
    let hasInvalidStep = false;
    for (const step of steps) {
      if (
        typeof step.icon !== 'string' ||
        typeof step.iconSet !== 'string' ||
        typeof step.label !== 'string' ||
        typeof step.duration !== 'number' ||
        step.duration <= 0
      ) {
        hasInvalidStep = true;
        break;
      }
    }

    if (hasInvalidStep) {
      logger.warn('[LoadingScreen] Invalid config, using defaults', { title, steps });
      validatedTitle = 'Loading...';
      validatedSteps = [];
    }
  }

  // Time-based color progression for 120s timeout
  // 0-60s: Blue (optimistic) → 60-120s: Amber (taking longer)
  const [startTime] = useState(Date.now());

  const getColorSchemeFromTime = (elapsedMs) => {
    if (elapsedMs < 60000) {
      // 0-60s: Blue - optimistic
      return {
        primary: '#2563EB',
        light: '#DBEAFE',
        lighter: '#BFDBFE',
      };
    }
    // 60-120s: Amber - taking longer (NO RED)
    return {
      primary: '#F59E0B',
      light: '#FEF3C7',
      lighter: '#FDE68A',
    };
  };

  const [elapsedTime, setElapsedTime] = useState(0);
  const colors = getColorSchemeFromTime(elapsedTime);

  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const pulseAnim = useState(new Animated.Value(1))[0];
  const timeoutRef = useRef(null);
  const lastAnnouncedProgress = useRef(0);

  // Update elapsed time for color progression
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      setElapsedTime(elapsed);
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [startTime]);

  // Calculate total duration
  const totalDuration = validatedSteps.reduce((acc, step) => acc + step.duration, 0);

  // Hang watchdog. If a caller opted in via `watchdog` and this screen is still
  // mounted after WATCHDOG_MS, the parent's navigation never fired — a silent
  // hang like the trial-email opt-in dead-end. Report it to Sentry once; clears
  // automatically when the screen unmounts (i.e. navigation succeeded).
  useEffect(() => {
    if (!watchdog?.name) return undefined;
    const id = setTimeout(() => {
      Sentry.captureException(new Error(`Loading screen stuck: ${watchdog.name}`), {
        level: 'error',
        tags: { type: 'loading_screen_hang', screen: watchdog.name },
        extra: { watchdogMs: WATCHDOG_MS, ...(watchdog.context || {}) },
      });
      logger.warn(`[LoadingScreen] Hang watchdog fired for "${watchdog.name}" after ${WATCHDOG_MS}ms`);
    }, WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [watchdog?.name]);

  // Set up 120-second timeout on mount
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setHasTimedOut(true);
      // Force completion after timeout to notify parent component
      if (!isComplete) {
        setIsComplete(true);
        if (onComplete) {
          onComplete(true); // Pass true to indicate this is a timeout
        }
      }
    }, 120000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isComplete, onComplete]);

  // Pulse animation for main icon
  useEffect(() => {
    if (!isComplete) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.2,
            duration: 1000,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
          }),
        ])
      ).start();
    }
  }, [isComplete, pulseAnim]);

  // Accessibility announcements for progress milestones
  useEffect(() => {
    const roundedProgress = Math.round(progress);
    const milestones = [25, 50, 75, 100];

    // Check if we've crossed a milestone since last announcement
    for (const milestone of milestones) {
      if (roundedProgress >= milestone && lastAnnouncedProgress.current < milestone) {
        AccessibilityInfo.announceForAccessibility(`Loading progress: ${milestone} percent`);
        lastAnnouncedProgress.current = milestone;
        break;
      }
    }
  }, [progress]);

  useEffect(() => {
    let stepStartTime = Date.now();
    let animationFrame;

    const updateProgress = () => {
      const elapsed = Date.now() - stepStartTime;
      const stepDuration = validatedSteps[currentStep].duration;
      const stepProgress = Math.min((elapsed / stepDuration) * 100, 100);

      const baseProgress = (currentStep / validatedSteps.length) * 100;
      const currentStepContribution = (stepProgress / 100) * (100 / validatedSteps.length);
      const totalProgress = baseProgress + currentStepContribution;

      setProgress(totalProgress);

      if (stepProgress >= 100) {
        if (currentStep < validatedSteps.length - 1) {
          setCurrentStep(currentStep + 1);
          stepStartTime = Date.now();
        } else {
          setIsComplete(true);
          if (onComplete) {
            onComplete(false); // Pass false to indicate natural completion
          }
          return;
        }
      }

      animationFrame = requestAnimationFrame(updateProgress);
    };

    if (validatedSteps.length > 0) {
      animationFrame = requestAnimationFrame(updateProgress);
    }

    return () => cancelAnimationFrame(animationFrame);
  }, [currentStep, validatedSteps, onComplete]);

  const renderIcon = (iconName, iconSet, color, size = 24) => {
    if (iconSet === 'MaterialCommunityIcons') {
      return <MaterialCommunityIcons name={iconName} size={size} color={color} />;
    }
    return <Ionicons name={iconName} size={size} color={color} />;
  };

  // Conditional container component for blur support
  const ContainerComponent = useBlurBackground ? BlurView : View;
  const containerProps = useBlurBackground
    ? {
        intensity: blurIntensity,
        tint: blurTint,
        accessibilityLiveRegion: 'polite',
        accessibilityLabel: `Loading progress: ${Math.round(progress)} percent`,
      }
    : {
        accessibilityLiveRegion: 'polite',
        accessibilityLabel: `Loading progress: ${Math.round(progress)} percent`,
      };

  return (
    <ContainerComponent style={styles.container} {...containerProps}>
      <View style={styles.card}>
        {/* Main Icon */}
        <View style={styles.iconContainer}>
          <View style={[styles.mainIconCircle, { backgroundColor: colors.light }]}>
            {renderIcon(mainIcon.name, mainIcon.iconSet, colors.primary, 40)}
          </View>
          {!isComplete && (
            <Animated.View
              style={[
                styles.pulseCircle,
                { transform: [{ scale: pulseAnim }], borderColor: colors.lighter },
              ]}
            />
          )}
        </View>

        {/* Title */}
        <Text style={styles.title}>{isComplete ? completionTitle : validatedTitle}</Text>
        <Text style={styles.subtitle}>{isComplete ? completionSubtitle : subtitle}</Text>

        {/* Progress Bar */}
        {showProgress && validatedSteps.length > 0 && (
          <View style={styles.progressContainer}>
            <View style={styles.progressBarBackground}>
              <View
                style={[
                  styles.progressBarFill,
                  { width: `${progress}%`, backgroundColor: colors.primary },
                ]}
              />
            </View>
            <View style={styles.progressTextContainer}>
              <Text style={styles.progressText}>{Math.round(progress)}%</Text>
            </View>
          </View>
        )}

        {/* Steps */}
        {validatedSteps.length > 0 && (
          <View style={styles.stepsContainer}>
            {validatedSteps.map((step, index) => {
              const isCurrentStep = index === currentStep;
              const isCompleted = index < currentStep || isComplete;

              return (
                <View
                  key={index}
                  style={[
                    styles.stepItem,
                    isCurrentStep && [styles.stepItemActive, { backgroundColor: colors.light }],
                  ]}
                >
                  <View
                    style={[
                      styles.stepIconCircle,
                      isCompleted && styles.stepIconCompleted,
                      isCurrentStep && [styles.stepIconCurrent, { backgroundColor: colors.light }],
                    ]}
                  >
                    {isCompleted ? (
                      <Ionicons name="checkmark-circle" size={20} color="#16A34A" />
                    ) : (
                      renderIcon(
                        step.icon,
                        step.iconSet,
                        isCurrentStep ? colors.primary : '#94A3B8',
                        20
                      )
                    )}
                  </View>
                  <Text
                    style={[
                      styles.stepLabel,
                      isCompleted && styles.stepLabelCompleted,
                      isCurrentStep && styles.stepLabelCurrent,
                    ]}
                  >
                    {hasTimedOut && progress >= 100 ? 'Almost there...' : step.label}
                  </Text>
                  {isCurrentStep && !isComplete && (
                    <View style={styles.dotsContainer}>
                      <View
                        style={[styles.dot, styles.dot1, { backgroundColor: colors.primary }]}
                      />
                      <View
                        style={[styles.dot, styles.dot2, { backgroundColor: colors.primary }]}
                      />
                      <View
                        style={[styles.dot, styles.dot3, { backgroundColor: colors.primary }]}
                      />
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}

        {/* Completion Message */}
        {isComplete && completionMessage && !hasTimedOut && (
          <View style={styles.completionMessage}>
            <Text style={styles.completionText}>{completionMessage}</Text>
          </View>
        )}

        {/* Timeout Message */}
        {hasTimedOut && progress >= 100 && (
          <View style={[styles.completionMessage, styles.timeoutMessage]}>
            <Text style={[styles.completionText, styles.timeoutText]}>Almost there...</Text>
          </View>
        )}
      </View>

      {/* Tips Section */}
      {!isComplete && showTips && tip && (
        <View style={styles.tipsContainer}>
          <Text style={styles.tipsText}>{tip}</Text>
        </View>
      )}
    </ContainerComponent>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 448,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    padding: 32,
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  mainIconCircle: {
    width: 80,
    height: 80,
    backgroundColor: '#DBEAFE',
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pulseCircle: {
    position: 'absolute',
    width: 80,
    height: 80,
    borderRadius: 40,
    borderWidth: 4,
    borderColor: '#BFDBFE',
    opacity: 0.75,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#1E293B',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    color: '#64748B',
    marginBottom: 32,
  },
  progressContainer: {
    marginBottom: 32,
  },
  progressBarBackground: {
    height: 12,
    backgroundColor: '#E2E8F0',
    borderRadius: 6,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#2563EB',
    borderRadius: 6,
  },
  progressTextContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  progressText: {
    fontSize: 16,
    color: '#475569',
  },
  remainingText: {
    fontSize: 16,
    color: '#64748B',
  },
  stepsContainer: {
    gap: 16,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#F8FAFC',
  },
  stepItemActive: {
    backgroundColor: '#EFF6FF',
    transform: [{ scale: 1.02 }],
  },
  stepIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#E2E8F0',
  },
  stepIconCompleted: {
    backgroundColor: '#DCFCE7',
  },
  stepIconCurrent: {
    backgroundColor: '#DBEAFE',
  },
  stepLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  stepLabelCompleted: {
    color: '#15803D',
  },
  stepLabelCurrent: {
    color: '#1D4ED8',
  },
  dotsContainer: {
    flexDirection: 'row',
    gap: 4,
    marginLeft: 'auto',
  },
  dot: {
    width: 6,
    height: 6,
    backgroundColor: '#2563EB',
    borderRadius: 3,
  },
  completionMessage: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#F0FDF4',
    borderWidth: 1,
    borderColor: '#BBF7D0',
    borderRadius: 8,
  },
  completionText: {
    textAlign: 'center',
    color: '#15803D',
    fontWeight: '500',
  },
  timeoutMessage: {
    backgroundColor: '#FEF3C7',
    borderColor: '#FDE68A',
  },
  timeoutText: {
    color: '#92400E',
  },
  tipsContainer: {
    marginTop: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: 8,
    padding: 16,
    width: '100%',
    maxWidth: 448,
  },
  tipsText: {
    fontSize: 12,
    color: '#475569',
    textAlign: 'center',
  },
  tipsBold: {
    fontWeight: '500',
  },
});
